import { useCallback, useMemo } from 'react';
import { GroupAPIResponse } from '../../../service/group/group.model';
import useCurrentWorkspace from '../../workspace/hooks/useCurrentWorkspace';
import useMyWorkspacesChildrenAndWorkspaces from '../../workspace/hooks/useMyWorkspacesChildrenAndWorkspaces';
import { NormalizedGroup } from '../GroupsAdapter';
import { getTypeFromGroup, parseLevelsFromHierarchy } from './getTypeFromGroup';

export function useGetTypeFromGroup(marketId?: string) {
  const { data: currentWorkspace } = useCurrentWorkspace(marketId);
  const { childWorkspacesById } = useMyWorkspacesChildrenAndWorkspaces({
    skip: !!marketId,
  });

  const levelsByWorkspaceId = useMemo(() => {
    const map = new Map<string, ReturnType<typeof parseLevelsFromHierarchy>>();

    if (currentWorkspace?.id) {
      map.set(
        currentWorkspace.id,
        parseLevelsFromHierarchy(
          currentWorkspace.settings?.['flexeserveUI.hierarchy']
        )
      );
    }

    Object.values(childWorkspacesById).forEach((workspace) => {
      map.set(
        workspace.id,
        parseLevelsFromHierarchy(workspace.settings?.['flexeserveUI.hierarchy'])
      );
    });

    return map;
  }, [childWorkspacesById, currentWorkspace?.id, currentWorkspace?.settings]);

  const defaultWorkspaceId = marketId ?? currentWorkspace?.id;

  return useCallback(
    (
      group?: GroupAPIResponse | NormalizedGroup,
      workspaceId?: string,
      isAdmin?: boolean
    ) => {
      const resolvedWorkspaceId =
        workspaceId ?? (isAdmin ? undefined : defaultWorkspaceId);
      const levels = resolvedWorkspaceId
        ? levelsByWorkspaceId.get(resolvedWorkspaceId)
        : undefined;

      return getTypeFromGroup(group, resolvedWorkspaceId, isAdmin, levels);
    },
    [defaultWorkspaceId, levelsByWorkspaceId]
  );
}

export { BusinessLayerGroupType } from './getTypeFromGroup';
